import { Theme } from '@mui/material';
import { createTheme } from '@mui/material/styles';

const primaryColor: string = '#2196f3';

declare module '@mui/material/styles' {
  interface Palette {
    enika: {
      main: string;
      poseidon: string;
      grey: string;
      greyHover: string;
    };
  }
  interface PaletteOptions {
    enika?: {
      main: string;
      poseidon: string;
      grey: string;
      greyHover: string;
    };
  }
}

export const theme: Theme = createTheme({
  palette: {
    primary: {
      main: primaryColor,
    },
    secondary: {
      main: '#ff9800',
    },
    success: {
      main: '#07bc0c',
    },
    warning: {
      main: '#ff9800',
    },
    enika: {
      main: '#dc241f',
      poseidon: '#cea225',
      grey: '#dbdbdb',
      greyHover: '#bdbdbd',
    },
  },
  typography: {
    h1: {
      fontSize: '20px',
      fontWeight: '600',
    },
    h2: {
      fontSize: '1em',
      fontWeight: '600',
    },
    h3: {
      fontSize: '0.95em',
      fontWeight: '600',
      marginBottom: '5px',
    },
    h6: {
      fontSize: '1em',
    },
    subtitle1: {
      fontSize: '0.85em',
      fontWeight: '600',
    },
    subtitle2: {
      fontSize: '0.9em',
      fontWeight: '400',
    },
  },
  components: {
    MuiButtonGroup: {
      styleOverrides: {
        root: { boxShadow: 'none' },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        contained: {
          textTransform: 'initial',
          fontSize: '13px',
          fontWeight: '600',
          color: '#fff',
        },
        outlined: {
          textTransform: 'initial',
          fontSize: '13px',
          fontWeight: '600',
        },
        text: {
          fontSize: '13px',
          textTransform: 'initial',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          '& > .MuiBox-root:first-of-type': {
            minHeight: '65px',
            display: 'flex',
            columnGap: '10px',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #e1e1e1',
            fontSize: '18px',
            padding: '15px 15px 12px 15px',
            fontWeight: '600',
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          '&:last-child': {
            paddingBottom: '16px',
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paperAnchorRight: {
          zIndex: 1030,
          overflow: 'hidden',
        },
      },
    },
    MuiListItemButton: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiSelect: {
      styleOverrides: {
        outlined: {
          fontSize: '0.9em',
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          '& .MuiSlider-thumb': {
            transition: 'none',
          },
          '& .MuiSlider-track': {
            transition: 'none',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #e1e1e1',
          '& .MuiBox-root': {
            padding: '50px',
          },
          '& .MuiButtonBase-root': {
            fontWeight: 600,
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          '& .MuiBox-root': {
            padding: '50px',
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderRadius: 0,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          backgroundColor: '#e1e1e1',
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          '& tr:nth-of-type(odd)': {
            backgroundColor: '#fff',
          },
          '& tr:nth-of-type(even) ': {
            backgroundColor: '#f9f9f9',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-readOnly': {
            '&:hover fieldset': {
              border: '1px solid rgba(0,0,0,0.23)',
              color: 'rgba(0,0,0,0.23)',
            },
            '&.Mui-focused fieldset': {
              border: '1px solid rgba(0,0,0,0.23)',
              color: 'rgba(0,0,0,0.23)',
            },
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        subtitle1: {
          '&.show': {
            color: primaryColor,
            display: 'block',
          },
        },
        h6: {
          '& .show-value': {
            color: '#cfcfcf',
            display: 'block',
            fontSize: '14px',
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: '#252525',
          fontWeight: '600',
          '&.Mui-focused': {
            color: '#252525',
          },
        },
      },
    },
  },
});
